import {HttpErrorResponse} from '@angular/common/http';
import {Component, ViewChild} from '@angular/core';
import {FormBuilder, FormControlStatus, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {IncidentTransaction, TransactionApiService, TransactionV2ApiService} from '@npck/af-frontend-api';
import {LocalizationService, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {catchError, debounceTime, distinctUntilChanged, filter, finalize, Observable, of, Subscription, throwError} from 'rxjs';
import {INCIDENT_ID, TRANSACTION_ID, TRANSACTION_INDEX} from '../../../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../../../layout/main-container/main-container.component';
import {Path} from '../../../../../path';
import {parseFieldExpression, showServerValidationErrors, ValidationErrorItem} from '../../../../../utils/server.field.validation.utils';
import {collectDisabledControls, isAnyFormFillStarted, restoreEnabledState} from '../../../../../utils/form.utils';
import {TransactionsForm} from '../../../create-edit/transaction-information-step/transaction-information-step.component';
import {
    createTransactionRequestV2,
    normalizeBlankStringsToUndefined,
} from '../../../create-edit/transaction-information-step/transcation-information/transaction-information.component';

@Component({
    selector: 'af-transaction-create-edit',
    standalone: false,
    templateUrl: './transaction-create-edit.component.html',
})
export class TransactionCreateEditComponent {
    @ViewChild('mainContainer') mainContainer: MainContainerComponent;
    Path = Path;
    form?: FormGroup<TransactionsForm>;
    incidentId: string;

    transactionId: string;
    transactionIndex: number;
    transactions: IncidentTransaction[] = [];
    private formInitializeSubscription: Subscription;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private transactionApiService: TransactionApiService,
        private transactionV2ApiService: TransactionV2ApiService,
        private localizationService: LocalizationService,
        private dialogService: DialogService,
        private routerService: RouterService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.params[INCIDENT_ID];
        this.transactionId = this.activatedRoute.snapshot.params[TRANSACTION_ID];
        this.transactionIndex = +this.activatedRoute.snapshot.queryParams[TRANSACTION_INDEX];
        this.form = this.formBuilder.group({});
        this.subscribeToFormInitializations();

        if (this.transactionId) {
            this.transactionApiService.getIncidentTransaction(this.incidentId, this.transactionId)
                .pipe(rxSpinner('initial'))
                .subscribe(response => this.transactions = [response]);
        }
    }

    filter = (path: string): boolean => {
        return path === Path.TRANSACTIONS;
    };

    breadcrumbCustomizer = (path: string): string => {
        if (this.incidentId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#IncidentId', {[INCIDENT_ID]: this.incidentId});
        }

        if (this.transactionId === path) {
            return this.localizationService
                .getLocalizedKey('i18n_af_labelIncidentTransactionIndex', {index: this.transactionId});
        }
        if (path === Path.TRANSACTIONS) {
            return '...';
        }
        return path;
    };

    customNavigateToPath = (path?: string): boolean | void => {
        if (path === Path.TRANSACTIONS) {
            this.routerService.navigate(`${Path.INCIDENTS}/${this.incidentId}`, {queryParamsHandling: 'merge'});
            return;
        }
        return true;
    };

    navigateBack = (): Observable<boolean> => {
        if ((!this.transactionId && isAnyFormFillStarted(this.form)) || (this.transactionId && this.form.dirty)) {
            return this.dialogService.openDialog({
                title: 'i18n_af_dialogCreateIncidentWarningBeforeExitTitle',
                subtitle: 'i18n_af_dialogCreateIncidentWarningBeforeExitSubtitle',
                buttonOkText: 'i18n_af_buttonExit',
                buttonCancelText: 'i18n_af_buttonCancel',
                isButtonOkAccent: true,
            }, {disableClose: true});
        } else {
            return of(true);
        }
    };

    save(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        if (this.transactionId && this.form.pristine) {
            return;
        }
        let request: Observable<any>;
        if (this.transactionId) {
            request = this.transactionV2ApiService.updateIncidentTransaction(this.incidentId,
                this.transactionId,
                normalizeBlankStringsToUndefined(createTransactionRequestV2(this.form.controls.transactions.controls[0])),
            );
        } else {
            request = this.transactionV2ApiService.createIncidentTransactions(
                this.incidentId,
                this.form.controls.addedTransactions.controls
                    .map(transaction => normalizeBlankStringsToUndefined(createTransactionRequestV2(transaction))));
        }
        const disabledControls = collectDisabledControls(this.form);
        this.form.disable({emitEvent: false});
        const serverValidationErrors: ValidationErrorItem[] = [];
        request
            .pipe(
                rxSpinner('saveEdit'),
                catchError(
                    (error: HttpErrorResponse): Observable<any> => {
                        const responseBody = error.error;
                        const validationErrors: ValidationErrorItem[] = responseBody?.errors;
                        if (error.status === 400 && validationErrors?.length) {
                            validationErrors.forEach((item, index, object) => {
                                if (item.code === 'PARTICIPANT_IDENTIFICATION_NUMBER_INVALID') {
                                    serverValidationErrors.push(item);
                                    delete validationErrors[index];
                                }
                            });
                        }
                        return throwError(() => error);
                    },
                ),
                finalize(() => {
                    restoreEnabledState(this.form, disabledControls);
                    showServerValidationErrors(serverValidationErrors, (item: ValidationErrorItem) => {
                        const expression = parseFieldExpression(item?.field);

                        switch (expression?.field) {
                            case 'sender.identificationNumber':
                                return (this.transactionId)
                                    ? this.form?.controls?.transactions?.controls[expression.index]?.controls?.sender?.controls?.iin
                                    : this.form?.controls?.addedTransactions?.controls[expression.index]?.controls?.sender?.controls?.iin;
                            case 'receiver.identificationNumber':
                                return (this.transactionId)
                                    ? this.form?.controls?.transactions?.controls[expression.index]?.controls?.receiver?.controls?.iin
                                    : this.form?.controls?.addedTransactions?.controls[expression.index]?.controls?.receiver?.controls?.iin;
                            default: return null;
                        }
                    });
                })
            )
            .subscribe(() => this.mainContainer.breadcrumbs.navigateBackSkipListener());
    }

    private subscribeToFormInitializations(): void {
        this.formInitializeSubscription = this.form.statusChanges.pipe(
            rxSpinner('initForms'),
            debounceTime(500),
            distinctUntilChanged(),
            filter((status: FormControlStatus) => status !== 'PENDING'),
        ).subscribe(() => {
            this.formInitializeSubscription.unsubscribe();
            this.form.markAsPristine();
        });
    }
}
